<template>
  <section ref="homeOutfits"
           class="home-outfits"
           aria-label="Shop by outfits on homepage">
    <div class="home-outfits-stage section-wrap">
      <div class="home-outfits-center"
           :style="{ maxHeight: cardsWrapStyle.minHeight }">
        <h2 class="home-outfits-title"
            v-for="(title, index) in homeOutfitsTitle"
            :key="index">
          {{ title }}
        </h2>
        <div class="home-outfits-sub">
          <p v-for="(title, index) in homeOutfitsSubTitle"
             :key="`sub-${index}`">
            {{ title }}
          </p>
        </div>
      </div>
    </div>

    <router-link class="home-outfits-cta"
                 to="/outfits">
      All Outfits
    </router-link>

    <div ref="homeOutfitsCards"
         class="home-outfits-cards section-wrap"
         :style="cardsWrapStyle">
      <div v-for="(o, oi) in displayOutfitDeck"
           :key="o.id"
           class="home-outfits-card"
           :class="{ 'is-visible': oi < state.outfitsShownCount }"
           :style="outfitCardStyle(o, oi)"
           @click="openDialog(o)">
        <div class="home-outfits-card-meta">
          <span>[{{ o.itemCount }} Items]</span>
          <span>
            <span style="margin-right: -9px">
              {{ o.currencySymbol || '$' }}
            </span>
            {{ o.totalUsd }}
          </span>
        </div>
        <div class="home-outfits-card-media">
          <img class="home-outfits-card-cover"
               :src="o.cover"
               :alt="o.title"
               loading="lazy" />
          <div class="home-outfits-card-mask"
               aria-hidden="true">
            <svg class="home-outfits-cross-svg"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 189 235"
                 preserveAspectRatio="none"
                 fill="none"
                 aria-hidden="true">
              <path stroke="currentColor"
                    stroke-dasharray="3 3"
                    stroke-opacity="0.5"
                    d="M94.5 3v230M187 117.5H0" />
              <path fill="currentColor"
                    d="M186 116h3v3h-3zM0 116h3v3H0zM93 0h3v3h-3zm0 232h3v3h-3z" />
              <circle cx="95"
                      cy="118"
                      r="5"
                      fill="currentColor" />
            </svg>
          </div>
        </div>
        <p class="home-outfits-card-title">
          {{ o.title }}
        </p>
      </div>
    </div>

    <HomeOutfitsDialog :visible="state.dialogOpen"
                       :selectPlatform="selectPlatform"
                       :outfit="currentOutfit"
                       :items="currentDialogItems"
                       @close="closeDialog" />
  </section>
</template>

<script>
import HomeOutfitsDialog from './dialog.vue'
import { reportGoodsClick } from '@/utils/goodsAction'

/**
 * Outfits 卡片布局配置：
 * - slots: 固定卡片模板位置（x/y）
 * - groupOffsetY: 超出模板数量时，每组向下平移高度
 * - wrapBaseTop/wrapBaseBottom: 容器上下预留高度
 */
const OUTFIT_LAYOUT_CFG = {
  pc: {
    slots: [
      { x: '19%', y: '360px' },
      { x: '58%', y: '250px' },
      { x: '89%', y: '480px' },
      { x: '13%', y: '880px' },
      { x: '49%', y: '760px' },
      { x: '80%', y: '1080px' },
    ],
    groupOffsetY: 1220,
    wrapBaseTop: 0,
    wrapBaseBottom: 300
  },
  mobile: {
    // 小屏固定三张模板位，且只展示前三条
    slots: [
      { x: 'calc(0% + 120px)', y: '180px' },
      { x: 'calc(100% - 120px)', y: '490px' },
      { x: 'calc(50%)', y: '800px' },
    ],
    groupOffsetY: 1000,
    wrapBaseTop: 0,
    wrapBaseBottom: 300
  }
}

/**
 * 首页 outfits 区块：卡片叠层 + 滚动显隐 + 弹窗展示
 */
export default {
  name: 'HomeOutfits',
  components: {
    HomeOutfitsDialog
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false
    },
    lockMobile: {
      type: Boolean,
      default: false
    },
    homeInfo: {
      type: Object,
      default: () => ({})
    },
    selectPlatform: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      /** 统一管理本组件 UI 状态 */
      state: {
        dialogOpen: false,
        activeOutfitId: '',
        outfitsReady: false,
        outfitsShownCount: 0
      },
      _outScroll: null,
      _outResize: null,
      _setOutfitsProgress: null,
      _outfitsRevealTimer: null,
      _scrollProgress: 0, // 滚动进度 0-1
      _outProgressRaf: 0
    }
  },
  computed: {
    layoutCfg() {
      return this.isMobile ? OUTFIT_LAYOUT_CFG.mobile : OUTFIT_LAYOUT_CFG.pc
    },
    homeOutfitsTitle() {
      return this.homeInfo?.homeContent?.title2?.split('##').filter(Boolean) || []
    },

    homeOutfitsSubTitle() {
      return this.homeInfo?.homeContent?.title3?.split('##').filter(Boolean) || []
    },

    outfitDeck() {
      const list = this.homeInfo?.goodsList?.Suit?.slice(0, 6) || []
      const { slots, groupOffsetY } = this.layoutCfg

      return list.map((item, i) => {
        const goods = this.parseGoodsJson(item?.goodsJson)
        const slot = slots[i % slots.length] || { x: '50%', y: '0px' }
        const groupIdx = Math.floor(i / Math.max(slots.length, 1))
        const baseY = Number.parseFloat(slot.y) || 0
        return {
          id: String(item?.id || `suit-${i}`),
          title: item?.goodsName || `Suit ${i + 1}`,
          cover: item?.goodsImage || '',
          currencySymbol: item?.currencySymbol || '$',
          totalUsd: Number(item?.price || 0).toFixed(2),
          dialogItems: goods,
          itemCount: goods.length || 1,
          x: slot.x,
          y: `${baseY + groupIdx * groupOffsetY}px`
        }
      })
    },

    displayOutfitDeck() {
      return this.outfitDeck
    },

    cardsWrapStyle() {
      const { wrapBaseTop, wrapBaseBottom } = this.layoutCfg

      // 根据实际最后一张卡片的位置计算高度
      const lastCard = this.outfitDeck[this.outfitDeck.length - 1]
      const lastCardY = lastCard ? Number.parseFloat(lastCard.y) || 0 : 0

      // 根据屏幕宽度获取卡片高度（与 CSS 同步）
      let cardHeight = 375
      if (this.isMobile) {
        cardHeight = 234
      } else {
        const w = window.innerWidth
        if (w < 1200) cardHeight = 287
      }

      return {
        minHeight: `${wrapBaseTop + lastCardY + cardHeight + wrapBaseBottom}px`
      }
    },
    currentOutfit() {
      return this.outfitDeck.find((o) => o.id === this.state.activeOutfitId) || {}
    },
    currentDialogItems() {
      return this.currentOutfit?.dialogItems || []
    }
  },
  watch: {
    isMobile() {
      this.$nextTick(() => {
        if (typeof this._setOutfitsProgress === 'function') this._setOutfitsProgress()
      })
    },
    lockMobile() {
      this.teardownOutfits()
      this.$nextTick(() => {
        this.bindOutfitsProgress()
      })
    },
    'outfitDeck.length'(len, prevLen) {
      // 处理接口异步回填：列表有数据后等待进入视口再触发动画。
      if (!len || len === prevLen) return
      this.state.outfitsReady = false
      this.state.outfitsShownCount = 0
      this.clearOutfitsRevealTimer()
      this.$nextTick(() => {
        this.tryRevealOutfits()
      })
    }
  },
  mounted() {
    this.bindOutfitsProgress()
    this.$nextTick(() => {
      this.tryRevealOutfits()
    })
  },
  beforeDestroy() {
    this.clearOutfitsRevealTimer()
    this.teardownOutfits()
    if (this._outProgressRaf) {
      cancelAnimationFrame(this._outProgressRaf)
      this._outProgressRaf = 0
    }
  },
  methods: {
    outfitCardStyle(o) {
      return {
        '--x': o.x,
        '--y': o.y
      }
    },
    /**
     * 进入可视区后按顺序逐张展示卡片。
     */
    startOutfitsSequentialReveal() {
      const total = this.displayOutfitDeck.length
      if (!total) return
      this.clearOutfitsRevealTimer()
      this.state.outfitsShownCount = 0
      this._outfitsRevealTimer = setInterval(() => {
        if (this.state.outfitsShownCount >= total) {
          this.clearOutfitsRevealTimer()
          return
        }
        this.state.outfitsShownCount += 1
      }, 350)
    },
    clearOutfitsRevealTimer() {
      if (!this._outfitsRevealTimer) return
      clearInterval(this._outfitsRevealTimer)
      this._outfitsRevealTimer = null
    },
    /**
     * 卡片容器进入视口后再触发入场，避免未到展示区就提前淡入。
     * 使用滚动位置计算，兼容 iOS 浏览器环境。
     */
    tryRevealOutfits() {
      if (this.state.outfitsReady) return
      const cards = this.$refs.homeOutfitsCards
      if (!cards || typeof cards.getBoundingClientRect !== 'function') return
      const rect = cards.getBoundingClientRect()
      const viewportH = window.innerHeight || document.documentElement.clientHeight || 0
      const triggerLine = viewportH * 0.9
      const isInView = rect.top <= triggerLine && rect.bottom >= 0
      if (isInView) {
        this.state.outfitsReady = true
        this.startOutfitsSequentialReveal()
      }
    },
    /**
     * 解析接口中的 goodsJson，结构异常时返回空数组。
     */
    parseGoodsJson(raw) {
      if (!raw) return []
      if (Array.isArray(raw)) return raw
      try {
        const arr = JSON.parse(raw)
        return Array.isArray(arr) ? arr : []
      } catch (e) {
        return []
      }
    },
    openDialog(outfit) {
      // 点击商品套装卡片，统计点击事件
      reportGoodsClick(outfit, 'suit')

      this.state.activeOutfitId = outfit.id
      this.state.dialogOpen = true
    },
    closeDialog() {
      this.state.dialogOpen = false
    },
    bindOutfitsProgress() {
      const section = this.$refs.homeOutfits
      if (!section) return
      const clamp = (n, a, b) => Math.max(a, Math.min(b, n))
      const setProgressRaw = () => {
        const rect = section.getBoundingClientRect()
        const total = rect.height + window.innerHeight
        const p = clamp((window.innerHeight - rect.top) / Math.max(total, 1), 0, 1)
        section.style.setProperty('--outfits-text-prog', String(p.toFixed(3)))
        this.tryRevealOutfits()
      }

      const setProgress = () => {
        if (this._outProgressRaf) return
        this._outProgressRaf = requestAnimationFrame(() => {
          this._outProgressRaf = 0
          setProgressRaw()
        })
      }

      this._setOutfitsProgress = setProgressRaw
      setProgressRaw()
      this._outScroll = setProgress
      this._outResize = setProgress
      window.addEventListener('scroll', this._outScroll, { passive: true })
      if (!this.lockMobile) {
        window.addEventListener('resize', this._outResize)
      }
    },
    teardownOutfits() {
      if (this._outScroll) window.removeEventListener('scroll', this._outScroll)
      if (this._outResize) window.removeEventListener('resize', this._outResize)
      if (this._outProgressRaf) {
        cancelAnimationFrame(this._outProgressRaf)
        this._outProgressRaf = 0
      }
      this._setOutfitsProgress = null
      this._outScroll = null
      this._outResize = null
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../styles/find-base.css';

.home-outfits-cards {
  overflow: hidden;
}

.home-outfits-card {
  cursor: pointer;
  opacity: 0;
  transform: translate3d(0, 56px, 0) scale(0.96);
  transition: opacity 0.7s ease-out, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: opacity, transform;
}

.home-outfits-card.is-visible {
  opacity: 1;
  transform: translate(-50%, -50%);
}
</style>
